import React, { useCallback } from 'react';
import PfCallOrigins from '../../models/enums/PfCallOrigins';
import mobxInjectSelect from '../utils/mobxInjectSelect';
import { CallChangeBang, MissedCallBang } from './';

type CallChangeLogProps = {
  message: {
    body: string;
    conversation: { counterPartyId: string };
    counterParty: {
      groupType: string;
      isRoleBot: boolean;
      p2pRecipient?: { $entityType: string; botUserId: string; memberIds: string[] };
    };
    counterPartyType: string;
    senderId: string;
    counterPartyId: string;
    group: { members: Record<string, unknown>[] };
  };
};

type MobxProps = {
  currentUserId: string;
  isGroupVideoCallEnabled: boolean;
  isGroupVoiceCallEnabled: boolean;
  isPFGroupVideoCallEnabled: boolean;
  isPFGroupVoiceCallEnabled: boolean;
  isProviderNetwork: boolean;
  videoCallSetUp: (
    entity: {
      $entityType: string;
      recipientId: string;
      recipientRoleId?: string;
      isVideo: boolean;
      members: Record<string, unknown>[];
      context: Record<string, unknown>;
    },
    type: 'Bang'
  ) => void;
};

function CallChangeLog({
  currentUserId,
  isGroupVideoCallEnabled,
  isGroupVoiceCallEnabled,
  isPFGroupVideoCallEnabled,
  isPFGroupVoiceCallEnabled,
  isProviderNetwork,
  message,
  videoCallSetUp,
}: CallChangeLogProps & MobxProps) {
  const { body, conversation, counterParty, counterPartyType, senderId } = message;
  const { groupType, p2pRecipient } = counterParty || {};
  const isVideoCall = body ? body.includes('Video') : false;
  const isGroup = counterPartyType && counterPartyType === 'group';
  const isCaller = senderId === currentUserId;
  const isRole = groupType === 'ROLE_P2P';
  const isRoleRecipient = isRole && p2pRecipient?.$entityType === 'role';
  const isEmptyRole = isRoleRecipient && !p2pRecipient?.memberIds?.length;

  const callAgain = useCallback(() => {
    let members: Record<string, unknown>[], recipientId;
    if (isGroup) {
      recipientId = message.counterPartyId;
      members = message.group.members;
    } else {
      recipientId = isCaller ? conversation.counterPartyId : senderId;
      members = [];
    }
    const context = { type: isGroup ? 'group' : 'account', id: conversation.counterPartyId };
    videoCallSetUp(
      {
        $entityType: isGroup ? 'group' : 'user',
        recipientId,
        recipientRoleId: isRoleRecipient ? p2pRecipient?.botUserId : undefined,
        isVideo: isVideoCall,
        members,
        context,
      },
      PfCallOrigins.BANG
    );
  }, [
    conversation.counterPartyId,
    isCaller,
    isGroup,
    isRoleRecipient,
    isVideoCall,
    message.counterPartyId,
    message.group,
    p2pRecipient,
    senderId,
    videoCallSetUp,
  ]);

  if (body && (body.startsWith('Missed Call') || body.startsWith('Missed Video Call'))) {
    return (
      <MissedCallBang
        body={body}
        callAgain={callAgain}
        isEmptyRole={isEmptyRole}
        isGroup={isGroup && !isRole}
        isProviderNetwork={isProviderNetwork}
        isGroupVideoCallEnabled={isGroupVideoCallEnabled}
        isGroupVoiceCallEnabled={isGroupVoiceCallEnabled}
        isPFGroupVideoCallEnabled={isPFGroupVideoCallEnabled}
        isPFGroupVoiceCallEnabled={isPFGroupVoiceCallEnabled}
        isVideoCall={isVideoCall}
      />
    );
  } else {
    return (
      <CallChangeBang
        body={body}
        callAgain={callAgain}
        isCaller={isCaller}
        isEmptyRole={isEmptyRole}
        isGroup={isGroup && !isRole}
        isProviderNetwork={isProviderNetwork}
        isGroupVideoCallEnabled={isGroupVideoCallEnabled}
        isGroupVoiceCallEnabled={isGroupVoiceCallEnabled}
        isPFGroupVideoCallEnabled={isPFGroupVideoCallEnabled}
        isPFGroupVoiceCallEnabled={isPFGroupVoiceCallEnabled}
        isVideoCall={isVideoCall}
      />
    );
  }
}

export default mobxInjectSelect<CallChangeLogProps, MobxProps>({
  callStore: [
    'isGroupVideoCallEnabled',
    'isGroupVoiceCallEnabled',
    'isPFGroupVideoCallEnabled',
    'isPFGroupVoiceCallEnabled',
    'videoCallSetUp',
  ],
  sessionStore: ['currentUserId'],
  networkStore: ['isProviderNetwork'],
})(CallChangeLog);
